/**
 * El motor del juego: escena, bucle, fisica, colisiones.
 *
 * Tres decisiones de rendimiento que marcan la diferencia en un Android de gama
 * media, que es el presupuesto del proyecto:
 *
 *  1. RECICLAJE. Los obstaculos y las monedas no se crean y destruyen: se
 *     sacan de una reserva y se devuelven cuando quedan detras. Crear mallas
 *     mientras el juego corre provoca recolecciones de basura, y una
 *     recoleccion a 60 fps es un tiron que se ve.
 *  2. SIN SOMBRAS. Un mapa de sombras cuesta una pasada de render entera. En su
 *     lugar hay una mancha oscura debajo del jugador, que es lo unico que el ojo
 *     necesita para saber a que altura esta.
 *  3. TOPE AL PIXEL RATIO. Un movil con pantalla 3x renderizaria nueve veces
 *     mas pixeles que uno 1x por la misma imagen. Se limita a 2.
 */

import {
  AmbientLight,
  BoxGeometry,
  Color,
  DirectionalLight,
  Fog,
  Euler,
  Group,
  IcosahedronGeometry,
  InstancedMesh,
  Matrix4,
  Quaternion,
  Mesh,
  MeshBasicMaterial,
  MeshLambertMaterial,
  Object3D,
  PerspectiveCamera,
  PlaneGeometry,
  Scene,
  Vector3,
  WebGLRenderer,
} from 'three';
import cfgNivel from '@compartido/nivel-config.json';
import { AVATAR_POR_DEFECTO, type Avatar } from './avatar';
import {
  ANCHO_CARRIL,
  LARGO_BIOMA,
  LARGO_SEGMENTO,
  biomaDeSegmento,
  generarSegmento,
  velocidadEn,
  type Bioma,
  type Carril,
  type Segmento,
  type TipoRecolectable,
} from './nivel';
import { PALETAS, COLORES, mezclarPaletas, type PaletaBioma } from './paleta';
import type { Corredor } from './personaje';
import { brillo, crearCielo, crearLuna } from './cielo';
import {
  Chispas,
  materialDecorado,
  materialVentanas,
  crearDestello,
  crearEstrellas,

  crearPlantillas,
  type Plantillas,
} from './mallas';
import {
  hayChoque,
  hayRecogida,
  puedeSaltar,
  type EstadoJugador,
  type TipoObstaculo,
} from './reglas';
import { Accion, Entrada } from './entrada';
import { audio } from './audio';
import { telegram } from '../net/telegram';

// --- Ajustes del jugador. No van en nivel-config.json porque no afectan a la
// --- generacion del nivel: el servidor no necesita conocerlos.
const SALTO_V0 = cfgNivel.jugador.saltoV0;
const GRAVEDAD = cfgNivel.jugador.gravedad;
const DUR_DESLIZAR = cfgNivel.jugador.duracionDeslizar;
const VEL_CAMBIO_CARRIL = cfgNivel.jugador.velocidadCambioCarril;

const DUR_IMAN = 7;
const DUR_X2 = 10;
const DUR_COHETE = cfgNivel.jugador.duracionCohete;
const RADIO_IMAN = 4.2;

/** Cuantos segmentos se mantienen generados por delante del jugador. */
const SEGMENTOS_ADELANTE = 7;

export interface EstadoPartida {
  distancia: number;
  monedas: number;
  velocidad: number;
  bioma: Bioma;
  combo: number;
  escudo: boolean;
  msX2: number;
  msIman: number;
  msCohete: number;
  vivo: boolean;
}

export interface ResultadoPartida {
  distancia: number;
  monedas: number;
  /**
   * Milisegundos JUGADOS, sin pausas ni anuncios. No es el reloj de pared.
   * El servidor acota la distancia con esto, asi que meter aqui el tiempo del
   * anuncio aflojaria la cota (ver DECISIONS.md D-012).
   */
  duracionMs: number;
  registroEntradas: string;
  /** Cuantos segmentos del nivel se recorrieron. El servidor lo usa para la cota. */
  segmentos: number;
  muertePor: 'barrera' | 'vela' | 'tiburon' | 'abandono' | null;
  /** Veces que revivio. La cuenta que vale es la del servidor; esta es informativa. */
  revivires: number;
}

/** Lo que se sabe en el momento de morir, antes de decidir si se revive. */
export interface Muerte {
  distancia: number;
  monedas: number;
  revivires: number;
  muertePor: 'barrera' | 'vela' | 'tiburon' | 'abandono' | null;
}

interface PiezaActiva {
  objeto: Object3D;
  tipo: string;
  carril: Carril;
  z: number;
  /** Para los recolectables: cuantas monedas vale. */
  valor: number;
  /** Ya se cogio o ya se choco: no vuelve a contar. */
  consumida: boolean;
}

export class Motor {
  private readonly escena = new Scene();
  private readonly camara: PerspectiveCamera;
  private readonly render: WebGLRenderer;
  private readonly plantillas: Plantillas;
  private readonly entrada: Entrada;
  private readonly chispas = new Chispas();
  private readonly destello: Mesh;

  private readonly luzAmbiente: AmbientLight;
  private readonly luzSol: DirectionalLight;
  /**
   * Luz de contorno: va DETRAS de la escena y dibuja un filo de color en el
   * borde de cada figura. Es el truco mas barato que existe para separar un
   * objeto del fondo, y la mitad de por que un juego parece caro.
   */
  private readonly luzContorno: DirectionalLight;
  private readonly cielo = crearCielo();
  private readonly luna = crearLuna();
  private readonly estrellas = crearEstrellas();
  private readonly niebla = new Fog(0x000000, 40, 160);
  /** Reutilizado en cada cambio de paleta: ver la nota de `aplicarPaleta`. */
  private readonly colorFondo = new Color(0x000000);
  /** Los dos railes de neon que enmarcan la pista. */
  private readonly railes: Mesh[] = [];

  private jugador!: Group;
  /** Las partes del personaje que se animan. Ver `animarJugador`. */
  private tabla!: Group;
  private cabeza!: Group;
  private brazoIzq!: Group;
  private brazoDer!: Group;
  /** El personaje 3D con esqueleto, cuando termina de bajarse. Null hasta entonces. */
  private corredor: Corredor | null = null;
  /** El dt del cuadro en curso, para el reloj de las animaciones. */
  private ultimoDt = 0;
  private sombra!: Mesh;
  private suelo!: Mesh;
  /** Las 80 rayas de los carriles, en un solo objeto por instancias. */
  private lineas!: InstancedMesh;
  /** Los 24 bloques del decorado lateral, en un solo objeto por instancias. */
  private decorados!: InstancedMesh;
  /** Las ventanas encendidas, encima de los mismos bloques. Solo en la ciudad. */
  private ventanas!: InstancedMesh;
  /** Remates, antenas, luces de aviso y carteles: el horizonte de la ciudad. */
  private remates!: InstancedMesh;
  private antenas!: InstancedMesh;
  private balizas!: InstancedMesh;
  private carteles!: InstancedMesh;
  /** El terreno ancho bajo la ciudad y los bordillos de la calzada. */
  private terreno!: Mesh;
  private readonly bordillos: Mesh[] = [];
  /**
   * La FORMA de cada pieza del decorado, con su z de partida.
   *
   * Se guarda para poder recolocar solo la z en cada cuadro, sin recalcular
   * escalas ni colores. Ver `recolocarEnZ`.
   */
  private formasEdificio: Array<{ m: Matrix4; base: number }> = [];
  private formasVentana: Array<{ m: Matrix4; base: number }> = [];
  private formasRemate: Array<{ m: Matrix4; base: number }> = [];
  private formasAntena: Array<{ m: Matrix4; base: number }> = [];
  private formasBaliza: Array<{ m: Matrix4; base: number }> = [];
  private formasCartel: Array<{ m: Matrix4; base: number }> = [];
  private formasNube: Array<{ m: Matrix4; base: number }> = [];
  private formasAsteroide: Array<{ m: Matrix4; base: number }> = [];
  /** Matriz de trabajo, para no crear una por pieza y por cuadro. */
  private readonly _m = new Matrix4();
  /** Nubes de la estratosfera y asteroides del espacio: grumos de bolas. */
  private nubes!: InstancedMesh;
  private asteroides!: InstancedMesh;

  // --- Reservas de objetos reciclados ---
  private readonly reserva = new Map<string, Object3D[]>();
  private activas: PiezaActiva[] = [];

  // --- Estado de la partida ---
  private seed = 0;
  private distancia = 0;
  private monedas = 0;
  private combo = 0;
  private msDesdeCombo = 0;
  private carril: Carril = 0;
  private xActual = 0;
  private y = 0;
  private vy = 0;
  private deslizando = 0;
  private escudo = false;
  private tIman = 0;
  private tX2 = 0;
  private tCohete = 0;
  private vivo = false;
  private pausada = false;
  private inicioMs = 0;
  /**
   * Milisegundos JUGADOS de verdad: solo cuentan los cuadros en los que el
   * jugador estaba vivo y sin pausa. Es lo que se le declara al servidor, en
   * vez del reloj de pared, porque el anuncio de revivir y las pausas no son
   * tiempo de juego y no pueden aflojar la cota de velocidad del antifraude.
   */
  private msJugados = 0;
  /** Revivires gastados en esta partida. La cuenta que manda es la del servidor. */
  private revivires = 0;
  /**
   * Multiplicador de monedas de toda la partida: 2 si el jugador vio el
   * anuncio del x2. **Lo concede el servidor**, aqui solo se aplica.
   * Es distinto del potenciador `x2` que sale en el nivel, que dura 10 s.
   */
  private multiplicadorBase = 1;
  /**
   * True cuando la partida ya se cerro y se mando el resultado al servidor.
   *
   * Es la guarda de `terminar`, en vez de `vivo`, porque desde que existe
   * revivir hay un estado nuevo: **muerto pero sin cerrar**, mientras el
   * jugador decide si ve el anuncio. Si la guarda siguiera siendo `vivo`, no
   * se podria cerrar desde ese estado y la partida quedaria abierta.
   */
  private cerrada = false;
  private muertePor: ResultadoPartida['muertePor'] = null;
  private ultimoSegmentoGenerado = -1;
  private biomaActual: Bioma = 'ciudad';
  private sacudida = 0;
  private tiempoJuego = 0;
  /** Empujoncito visual al chocar contra el borde de la pista. Vuelve solo a 0. */
  private empujeBorde = 0;

  private recordPersonal = 0;
  private avisoCasiDado = false;

  private rafId = 0;
  private ultimoCuadro = 0;

  // --- Resolucion adaptativa (ver `ajustarResolucion`) ---
  /** Techo de resolucion: el del aparato, con tope de 2. */
  private escalaMaxima = 2;
  /** La resolucion de ahora mismo. Entre 0,75 y el techo. */
  private escalaPixeles = 1.5;
  private msMedidos = 0;
  private cuadrosMedidos = 0;
  private ultimoAjuste = 0;
  /** Milisegundos por cuadro justo antes de la ultima bajada. 0 = no hubo. */
  private msAntesDeBajar = 0;
  private escalaAntesDeBajar = 1.5;
  /**
   * True cuando se comprobo que bajar la resolucion NO mejora los cuadros. A
   * partir de ahi no se vuelve a intentar: emborronar el juego sin ganar nada
   * es lo peor de los dos mundos.
   */
  private bajarNoSirve = false;

  /** Se llama en cada cambio relevante para que la interfaz se entere. */
  alCambiarEstado: ((e: EstadoPartida) => void) | null = null;
  /**
   * El jugador ha muerto y la partida **sigue abierta**. La interfaz decide:
   * ofrecer revivir (y llamar a `revivir`) o cerrar (y llamar a `rendirse`).
   */
  alMorir: ((m: Muerte) => void) | null = null;
  alTerminar: ((r: ResultadoPartida) => void) | null = null;
  /** Se llama cuando el jugador casi batia su record. Dispara el "casi lo logras". */
  alCasiRecord: (() => void) | null = null;

  constructor(
    private readonly lienzo: HTMLCanvasElement,
    /**
     * El avatar con el que se juega. Viene del perfil, que ya esta cargado
     * cuando se construye el motor (la primera partida es posterior a `/me`).
     */
    private avatar: Avatar = AVATAR_POR_DEFECTO
  ) {
    // El techo de resolucion. De aqui para abajo manda `ajustarResolucion`, que
    // la sube y la baja sola segun lo que tarde de verdad en pintar un cuadro.
    //
    // Se arranca a 1,5 y no al maximo a proposito: es mejor empezar fluido y
    // ganar nitidez al segundo que empezar a tirones. Los primeros segundos de
    // la primera partida son los que deciden si alguien sigue jugando.
    this.escalaMaxima = Math.min(window.devicePixelRatio || 1, 2);
    this.escalaPixeles = Math.min(this.escalaMaxima, 1.5);

    this.render = new WebGLRenderer({
      canvas: lienzo,
      // El antialias cuesta fps y en pantallas densas no se nota. Solo en 1x.
      antialias: this.escalaMaxima < 1.5,
      powerPreference: 'high-performance',
      alpha: false,
    });
    this.render.setPixelRatio(this.escalaPixeles);
    this.render.shadowMap.enabled = false;

    this.camara = new PerspectiveCamera(62, 1, 0.1, 260);
    this.camara.position.set(0, 4.4, 7.2);
    this.camara.lookAt(0, 1.4, -8);

    this.escena.fog = this.niebla;
    this.luzAmbiente = new AmbientLight(0xffffff, 0.8);

    // La luz principal, desde delante y arriba a la izquierda: da el volumen.
    this.luzSol = new DirectionalLight(0xffffff, 0.9);
    this.luzSol.position.set(-6, 14, 4);

    // La de contorno, desde DETRAS y abajo a la derecha, en el color del bioma.
    // Es la que pone el filo luminoso en el borde de las figuras. Dos luces
    // direccionales cuestan practicamente lo mismo que una (no hay sombras que
    // calcular) y la diferencia que hacen es enorme.
    this.luzContorno = new DirectionalLight(0xff3d81, 1.1);
    this.luzContorno.position.set(7, 5, -12);

    this.escena.add(
      this.luzAmbiente,
      this.luzSol,
      this.luzContorno,
      this.cielo.malla,
      this.luna.malla,
      this.estrellas,
      this.chispas.objeto
    );

    this.destello = crearDestello();
    this.camara.add(this.destello);
    this.escena.add(this.camara);

    this.plantillas = crearPlantillas();
    this.construirPista();
    this.construirJugador();

    this.entrada = new Entrada(lienzo);
    this.redimensionar();
    window.addEventListener('resize', this.redimensionar);
    telegram.alCambiarViewport(this.redimensionar);
    document.addEventListener('visibilitychange', this.alCambiarVisibilidad);
  }

  // ------------------------------------------------------------------
  // Construccion
  // ------------------------------------------------------------------

  private construirPista(): void {
    // --- El terreno ---
    //
    // Un plano ANCHO debajo de todo. Hasta el 22-oct-2026 no existia: solo
    // estaba la calzada, de 11,6 de ancho, y los edificios estan a 9-16 del
    // centro. O sea que **los edificios flotaban en el aire**, sin nada
    // debajo. No se notaba de noche porque el fondo era negro; de dia, con el
    // cielo azul, se ve perfectamente que estan colgados.
    //
    // Va un pelin por debajo de la calzada para que no peleen por los mismos
    // pixeles.
    this.terreno = new Mesh(
      new PlaneGeometry(140, 400),
      new MeshLambertMaterial({ color: 0x8d9aa8 })
    );
    this.terreno.rotation.x = -Math.PI / 2;
    this.terreno.position.y = -0.04;
    this.escena.add(this.terreno);

    // La calzada, encima del terreno. Lo que sobresale a los lados hace de
    // acera, y es lo que ata la ciudad al suelo.
    this.suelo = new Mesh(
      new PlaneGeometry(ANCHO_CARRIL * 3 + 5, 400),
      new MeshLambertMaterial({ color: 0x2a2f3d })
    );
    this.suelo.rotation.x = -Math.PI / 2;
    this.escena.add(this.suelo);

    // El bordillo: dos tiras claras donde acaba la calzada. Dan la linea que
    // separa calle de acera, que es lo que hace que se lea como una calle.
    for (const x of [-(ANCHO_CARRIL * 3 + 5) / 2, (ANCHO_CARRIL * 3 + 5) / 2]) {
      const bordillo = new Mesh(
        new BoxGeometry(0.5, 0.22, 400),
        new MeshLambertMaterial({ color: 0xd8dde3 })
      );
      bordillo.position.set(x, 0.09, -120);
      this.bordillos.push(bordillo);
      this.escena.add(bordillo);
    }

    // Las lineas de los carriles: lo que da la sensacion de velocidad.
    //
    // **Las 80 rayas son UN solo objeto dibujado por instancias.** Antes eran
    // 80 mallas sueltas, y cada malla suelta es una llamada de dibujo: 80 de
    // las 376 que tenia el juego por cuadro, para pintar unas rayas de fondo.
    // Las llamadas de dibujo son el cuello de botella de este juego (10.000
    // triangulos no son nada; 376 llamadas si), y en un movil cuestan todavia
    // mas que aqui.
    //
    // Las posiciones se fijan UNA vez y no se vuelven a tocar: como las rayas
    // se repiten cada 8 metros, mover el objeto entero entre 0 y 8 da
    // exactamente la misma imagen que recolocarlas una a una.
    this.lineas = new InstancedMesh(
      new BoxGeometry(0.12, 0.02, 3.2),
      new MeshBasicMaterial({ color: 0x00e5ff }),
      80
    );
    const m = new Matrix4();
    for (let fila = 0; fila < 40; fila++) {
      for (let lado = 0; lado < 2; lado++) {
        const x = lado === 0 ? -ANCHO_CARRIL / 2 : ANCHO_CARRIL / 2;
        m.makeTranslation(x, 0.02, fila * 8);
        this.lineas.setMatrixAt(fila * 2 + lado, m);
      }
    }
    this.lineas.instanceMatrix.needsUpdate = true;
    // Nunca se descarta por quedar fuera de camara: el objeto abarca toda la
    // pista y three no sabe calcular su volumen envolvente bien al moverlo.
    this.lineas.frustumCulled = false;
    this.escena.add(this.lineas);

    // Los railes de los bordes: dos tiras continuas de neon que enmarcan la
    // pista. Hacen tres cosas a la vez por el precio de dos cajas:
    //
    //   - dicen donde acaba lo jugable, que antes habia que adivinar
    //   - dan dos lineas de fuga que llevan el ojo al horizonte, que es lo que
    //     hace que una captura de pantalla se entienda de un vistazo
    //   - separan la pista oscura del fondo oscuro
    for (const x of [-3.62, 3.62]) {
      const rail = new Mesh(
        new BoxGeometry(0.14, 0.14, 400),
        new MeshBasicMaterial({ color: 0xff3d81 })
      );
      rail.position.set(x, 0.07, -120);
      this.railes.push(rail);
      this.escena.add(rail);
    }

    // Decorado lateral: 24 bloques que se reciclan. Edificios en la ciudad,
    // nubes en la estratosfera, asteroides en el espacio: la misma caja con
    // otra escala y otro color.
    //
    // Tambien por instancias, y por lo mismo que las rayas: eran 24 cubos mas
    // 24 cubos de ventanas, 48 llamadas de dibujo para el fondo. Ahora son 2.
    // **Una caja no puede ser a la vez edificio, nube y asteroide.** Lo era, y
    // por eso en la estratosfera las nubes parecian piedras y en el espacio los
    // asteroides parecian cajas. Ahora hay tres objetos con tres formas, y solo
    // se ensena el del bioma en el que estas: siguen siendo 2 llamadas de
    // dibujo, porque los otros dos estan ocultos.
    const cubo = new BoxGeometry(1, 1, 1);
    this.decorados = new InstancedMesh(cubo, materialDecorado(), 24);
    this.decorados.frustumCulled = false;
    this.escena.add(this.decorados);

    // Las ventanas son el MISMO cubo un pelin mas grande, encima.
    this.ventanas = new InstancedMesh(cubo, materialVentanas(), 24);
    this.ventanas.frustumCulled = false;
    this.escena.add(this.ventanas);

    // Remates, antenas, luces de aviso y carteles de neon: lo que convierte una
    // fila de cajas en un horizonte. Cuatro objetos mas, cuatro llamadas mas.
    this.remates = new InstancedMesh(cubo, materialDecorado(), 24);
    this.antenas = new InstancedMesh(cubo, materialDecorado(), 24);
    // Las balizas y los carteles van sin luz: son luces, no cosas iluminadas.
    this.balizas = new InstancedMesh(
      cubo,
      new MeshBasicMaterial({ color: 0xff3b30 }),
      24
    );
    this.carteles = new InstancedMesh(
      cubo,
      new MeshBasicMaterial({ color: 0xffffff }),
      24
    );
    for (const o of [this.remates, this.antenas, this.balizas, this.carteles]) {
      o.frustumCulled = false;
      this.escena.add(o);
    }

    // Nubes y asteroides: bolas de pocas caras. Tres por sitio, descolocadas
    // entre si, para que sean grumos y no pelotas. Son 72 instancias pero una
    // sola llamada de dibujo.
    this.nubes = new InstancedMesh(new IcosahedronGeometry(1, 1), materialDecorado(), 72);
    this.nubes.frustumCulled = false;
    this.nubes.visible = false;
    this.escena.add(this.nubes);

    // Los asteroides, con menos caras: tienen que verse angulosos y de piedra,
    // justo lo contrario que las nubes.
    this.asteroides = new InstancedMesh(new IcosahedronGeometry(1, 0), materialDecorado(), 72);
    this.asteroides.frustumCulled = false;
    this.asteroides.visible = false;
    this.escena.add(this.asteroides);

    this.recolocarDecorados();
  }

  /**
   * Cambia el avatar del personaje que ya esta en la escena.
   *
   * Si solo cambia la ropa, el pelo o la piel, se repinta en caliente. Si
   * cambia el CUERPO hay que traerse otro fichero, asi que se vuelve a cargar
   * el personaje entero y se sustituye cuando llegue.
   *
   * Si el personaje 3D todavia no habia llegado no hace nada, y tampoco hace
   * falta: cuando llegue lo hara con `this.avatar`, que ya es el nuevo.
   */
  cambiarAvatar(avatar: Avatar): void {
    const anterior = this.avatar;
    this.avatar = avatar;
    const c = this.corredor;
    if (!c) return;

    if (c.cuerpo === avatar.cuerpo) {
      c.cambiarAspecto(avatar);
      return;
    }

    void import('./personaje')
      .then(({ cargarCorredor }) => cargarCorredor(avatar))
      .then((nuevo) => {
        // Entre que se pidio y llego, el jugador puede haber elegido otra cosa.
        // Si pasa, lo que vale es lo ultimo que eligio, no lo que acaba de
        // llegar.
        if (!nuevo || this.avatar !== avatar) return;
        const viejo = this.corredor;
        if (viejo) this.jugador.remove(viejo.objeto);
        this.corredor = nuevo;
        this.jugador.add(nuevo.objeto);
        nuevo.poner(nuevo.clips.CORRER);
      })
      .catch(() => {
        // Si no se pudo traer el otro cuerpo, se sigue con el que hay puesto.
        this.avatar = anterior;
      });
  }

  private construirJugador(): void {
    this.jugador = this.plantillas.jugador.clone();
    this.escena.add(this.jugador);

    // Las partes que se animan. Se buscan UNA vez: `getObjectByName` recorre el
    // arbol, y hacerlo sesenta veces por segundo seria tirar tiempo.
    this.tabla = this.jugador.getObjectByName('tabla') as Group;
    this.cabeza = this.jugador.getObjectByName('cabeza') as Group;
    this.brazoIzq = this.jugador.getObjectByName('brazoIzq') as Group;
    this.brazoDer = this.jugador.getObjectByName('brazoDer') as Group;

    // El personaje bueno se pide por detras. El juego ya esta jugable con el
    // de siempre, asi que si tarda o no llega, no se nota (ver `personaje.ts`).
    //
    // La importacion es DINAMICA: `personaje.ts` arrastra el sistema de
    // animacion de three, y con un import normal eso entraria en la primera
    // descarga aunque el modelo no llegara nunca.
    void import('./personaje')
      .then(({ cargarCorredor }) => cargarCorredor(this.avatar))
      .then((c) => {
        if (!c) return;
        this.corredor = c;
        // Entra colgado del mismo objeto que ya se mueve, se inclina y se
        // escala: asi hereda todo lo que el motor ya le hacia al personaje
        // viejo sin tener que repetirlo.
        this.jugador.add(c.objeto);
        // Y el de siempre se apaga, no se borra: si algun dia hay que volver,
        // esta ahi.
        for (const hijo of this.jugador.children) {
          if (hijo !== c.objeto) hijo.visible = false;
        }
        c.poner(c.clips.CORRER);
      });

    // La mancha de sombra: un plano oscuro que se encoge con la altura. Es lo
    // unico que hace falta para saber cuanto falta para caer.
    // La sombra lleva la textura de degradado redondo: sin ella es un CUADRADO
    // gris. En la ciudad de noche no se notaba porque el suelo era casi negro;
    // sobre el asfalto claro del dia es un rectangulo que sigue al personaje.
    this.sombra = new Mesh(
      new PlaneGeometry(1.5, 1.5),
      new MeshBasicMaterial({
        map: brillo(),
        color: 0x000000,
        transparent: true,
        opacity: 0.34,
        depthWrite: false,
      })
    );
    this.sombra.rotation.x = -Math.PI / 2;
    this.escena.add(this.sombra);
  }

  /**
   * Los gestos del personaje.
   *
   * **Por que existe esto.** Antes, cambiar de carril solo inclinaba el muneco
   * entero unos quince grados. El dueno lo probo y dijo que el avatar no hace
   * gestos al moverse de carril, y tenia razon: un cuerpo rigido que se ladea
   * no es un gesto, es una rotacion.
   *
   * Lo que se mueve ahora, y por que cada cosa:
   *
   *  - **La cabeza mira hacia donde va.** Es el gesto que mas dice de todos,
   *    porque mirar hacia donde vas es lo primero que hace una persona al
   *    girar. Si solo se pudiera animar una cosa, seria esta.
   *  - **La tabla gira MAS que el cuerpo.** En una tabla de verdad el cuerpo se
   *    queda relativamente vertical y la tabla se tumba debajo. Esa diferencia
   *    entre las dos es lo que hace que un giro se sienta como un giro.
   *  - **Los brazos hacen contrapeso**, uno sube y el otro baja, como quien
   *    guarda el equilibrio.
   *  - **Al saltar se encoge y al deslizarse se estira hacia atras**, que es lo
   *    que hace que los dos movimientos se lean distintos de lejos.
   *  - **Y respira.** Un balanceo pequenisimo cuando no pasa nada: sin el, el
   *    personaje parece una estatua pegada a la pantalla.
   *
   * Todo con `lerp` hacia el objetivo en vez de saltos: lo que hace que un
   * gesto parezca vivo no es la postura final, es como se llega a ella.
   */
  /**
   * Elige la animacion del personaje 3D segun lo que esta pasando.
   *
   * El orden de las comprobaciones es el orden de importancia: lo que el
   * jugador acaba de hacer manda sobre lo que estaba haciendo. Rodar tapa
   * saltar, saltar tapa correr.
   */
  private animarCorredor(dt: number): void {
    const c = this.corredor;
    if (!c) return;

    c.mezclador.update(dt);

    if (!this.vivo) {
      c.poner(c.clips.MORIR, { unaVez: true });
      return;
    }
    if (this.deslizando > 0) {
      c.poner(c.clips.RODAR, { unaVez: true });
      return;
    }
    if (this.y > 0.1) {
      // Subiendo o bajando: dos poses distintas para el mismo salto, que es lo
      // que hace que se lea la cima.
      c.poner(this.vy > 0 ? c.clips.SALTO_SUBIR : c.clips.SALTO_AIRE);
      return;
    }
    // Corriendo. La animacion acelera con el juego: a 34 m/s corriendo al
    // ritmo de 11 m/s se ve como si patinara.
    const v = velocidadEn(this.distancia);
    c.poner(c.clips.CORRER, { velocidad: Math.max(0.8, Math.min(1.7, v / 16)) });
  }

  private animarJugador(): void {
    if (!this.tabla) return;

    // Cuanto falta para llegar al carril, de -1 a 1. Es la senal de "estoy
    // girando" y de hacia donde.
    const destino = this.carril * ANCHO_CARRIL;
    const giro = Math.max(-1, Math.min(1, (destino - this.xActual) / ANCHO_CARRIL));

    const t = this.tiempoJuego;
    const enElAire = this.y > 0.05;
    const deslizando = this.deslizando > 0;

    // --- La tabla: se tumba el triple que el cuerpo ---
    this.tabla.rotation.z += (giro * 0.85 - this.tabla.rotation.z) * 0.22;
    this.tabla.rotation.y += (-giro * 0.35 - this.tabla.rotation.y) * 0.2;
    // Flota: sube y baja un pelin sobre la pista.
    this.tabla.position.y = Math.sin(t * 5.5) * 0.025;

    // --- La cabeza: mira hacia donde va ---
    this.cabeza.rotation.y += (giro * 0.75 - this.cabeza.rotation.y) * 0.18;
    this.cabeza.rotation.z += (giro * 0.3 - this.cabeza.rotation.z) * 0.18;

    // --- Los brazos: contrapeso ---
    //
    // Al aire se suben los dos (el gesto de quien salta), deslizandose se van
    // los dos atras, y girando uno sube y el otro baja.
    const base = 0.5;
    const objIzq = enElAire ? -0.9 : deslizando ? 1.5 : base - giro * 0.9;
    const objDer = enElAire ? -0.9 : deslizando ? 1.5 : base + giro * 0.9;
    this.brazoIzq.rotation.x += (objIzq - this.brazoIzq.rotation.x) * 0.2;
    this.brazoDer.rotation.x += (objDer - this.brazoDer.rotation.x) * 0.2;
    // Y se abren hacia fuera al girar, para hacer equilibrio.
    const abre = Math.abs(giro) * 0.5;
    this.brazoIzq.rotation.z += (0.32 + abre - this.brazoIzq.rotation.z) * 0.2;
    this.brazoDer.rotation.z += (-0.32 - abre - this.brazoDer.rotation.z) * 0.2;

    // --- Respira ---
    // Solo cuando no esta haciendo nada: si se sumara a los demas gestos, los
    // emborronaria.
    const quieto = 1 - Math.min(1, Math.abs(giro) * 2);
    this.jugador.position.y += Math.sin(t * 4.2) * 0.012 * quieto;
  }

  /**
   * Pone cada pieza del decorado en su z, dando la vuelta por detras.
   *
   * `((base + d) % 264) - 250` hace que la pieza avance con el jugador y, al
   * pasar de largo, reaparezca a 250 metros por delante. El salto ocurre
   * **detras de la camara**, que es la diferencia entre reciclar y parpadear.
   */
  private recolocarEnZ(d: number): void {
    const objetos: Array<[InstancedMesh, Array<{ m: Matrix4; base: number }>]> = [
      [this.decorados, this.formasEdificio],
      [this.ventanas, this.formasVentana],
      [this.remates, this.formasRemate],
      [this.antenas, this.formasAntena],
      [this.balizas, this.formasBaliza],
      [this.carteles, this.formasCartel],
      [this.nubes, this.formasNube],
      [this.asteroides, this.formasAsteroide],
    ];
    for (const [objeto, formas] of objetos) {
      if (!objeto.visible || formas.length === 0) continue;
      for (let i = 0; i < formas.length; i++) {
        const f = formas[i]!;
        this._m.copy(f.m);
        const e = this._m.elements;
        e[14] = ((f.base + d) % 264) - 250;
        objeto.setMatrixAt(i, this._m);
      }
      objeto.instanceMatrix.needsUpdate = true;
    }
  }

  private recolocarDecorados(): void {
    const p = PALETAS[this.biomaActual];
    const esCiudad = this.biomaActual === 'ciudad';
    const esEspacio = this.biomaActual === 'espacio';

    this.decorados.visible = esCiudad;
    this.ventanas.visible = esCiudad;
    this.remates.visible = esCiudad;
    this.antenas.visible = esCiudad;
    this.balizas.visible = esCiudad;
    this.carteles.visible = esCiudad;
    this.nubes.visible = this.biomaActual === 'estratosfera';
    this.asteroides.visible = esEspacio;

    if (esCiudad) this.colocarEdificios(p);
    else this.colocarGrumos(esEspacio ? this.asteroides : this.nubes, p, esEspacio);
  }

  /**
   * Los edificios de la ciudad.
   *
   * Eran cajas a secas, y una fila de cajas del mismo tipo no se lee como una
   * ciudad por muchas ventanas que tenga: lo que hace una ciudad es que los
   * remates sean distintos. Asi que cada edificio lleva, ademas del cuerpo:
   *
   *  - un **remate** arriba: una caja mas estrecha, como los escalonados de los
   *    rascacielos. Es lo que rompe la linea recta del tejado.
   *  - una **antena** con su luz roja de aviso a aeronaves. Cuesta dos cajas
   *    finas y es lo que mas dice "esto es alto".
   *  - un **cartel de neon** en algunos, mirando a la pista.
   *
   * Todo con instancias: el barrio entero son 6 llamadas de dibujo.
   */
  private colocarEdificios(p: PaletaBioma): void {
    const m = new Matrix4();
    const color = new Color();
    const neon = new Color();

    // Se guarda la FORMA de cada pieza (escala y posicion en x/y) junto con su
    // z de partida. Luego, en cada cuadro, solo se cambia la z. Asi cada
    // edificio conserva lo que es mientras da la vuelta.
    this.formasEdificio = [];
    this.formasVentana = [];
    this.formasRemate = [];
    this.formasAntena = [];
    this.formasBaliza = [];
    this.formasCartel = [];

    for (let i = 0; i < 24; i++) {
      const lado = i % 2 === 0 ? -1 : 1;
      const fila = Math.floor(i / 2);
      // La z es la del patron, SIN el desplazamiento: ese lo lleva el objeto
      // entero en cada cuadro, igual que las rayas de los carriles.
      const z = fila * 22;
      // Mas variedad de alturas que antes (era 6-24): una ciudad con todos los
      // edificios parecidos se lee como una valla, no como un horizonte.
      const alto = 5 + ((i * 11) % 26);
      const ancho = 3.5 + ((i * 3) % 5);
      const fondo = 4 + ((i * 5) % 6);
      const x = lado * (9 + ((i * 3) % 7));

      m.makeScale(ancho, alto, fondo);
      m.setPosition(x, alto / 2, z);
      this.decorados.setMatrixAt(i, m);
      this.formasEdificio.push({ m: m.clone(), base: z });

      // Las ventanas, un 1% mas grandes para que no peleen con la cara del
      // edificio por el mismo pixel (el clasico parpadeo de dos superficies
      // a la misma profundidad).
      m.makeScale(ancho * 1.01, alto * 1.01, fondo * 1.01);
      m.setPosition(x, alto / 2, z);
      this.ventanas.setMatrixAt(i, m);
      this.formasVentana.push({ m: m.clone(), base: z });

      color.setHex(p.decorado[i % p.decorado.length] as number);
      this.decorados.setColorAt(i, color);

      // --- El remate ---
      // Solo en los altos: en un edificio bajo no se veria y quedaria como un
      // bulto raro.
      const conRemate = alto > 14;
      const altoRemate = conRemate ? 2 + ((i * 7) % 4) : 0;
      m.makeScale(ancho * 0.55, altoRemate, fondo * 0.55);
      m.setPosition(x, alto + altoRemate / 2, z);
      this.remates.setMatrixAt(i, m);
      this.formasRemate.push({ m: m.clone(), base: z });
      this.remates.setColorAt(i, color);

      // --- La antena y su luz ---
      const conAntena = conRemate && i % 3 === 0;
      const altoAntena = conAntena ? 3 + ((i * 5) % 4) : 0;
      const baseAntena = alto + altoRemate;
      m.makeScale(0.12, altoAntena, 0.12);
      m.setPosition(x, baseAntena + altoAntena / 2, z);
      this.antenas.setMatrixAt(i, m);
      this.formasAntena.push({ m: m.clone(), base: z });

      // La luz roja de la punta: el detalle que hace que una antena parezca una
      // antena y no un palo.
      m.makeScale(conAntena ? 0.34 : 0, conAntena ? 0.34 : 0, conAntena ? 0.34 : 0);
      m.setPosition(x, baseAntena + altoAntena, z);
      this.balizas.setMatrixAt(i, m);
      this.formasBaliza.push({ m: m.clone(), base: z });

      // --- El cartel de neon ---
      // En un tercio de los edificios, pegado a la cara que mira a la pista.
      const conCartel = i % 3 === 1;
      const altoCartel = conCartel ? 2 + ((i * 3) % 3) : 0;
      m.makeScale(conCartel ? 0.3 : 0, altoCartel, fondo * 0.55);
      m.setPosition(
        x - lado * (ancho / 2 + 0.2),
        alto * 0.55 + ((i * 4) % 5),
        z
      );
      this.carteles.setMatrixAt(i, m);
      this.formasCartel.push({ m: m.clone(), base: z });
      // Colores de neon de rotulo, alternando. Son los unicos puntos calidos
      // del barrio y por eso se ven tanto.
      neon.setHex([0xff3d81, 0x00e5ff, 0xffd24a, 0x7bdc5a][i % 4] as number);
      this.carteles.setColorAt(i, neon);
    }

    for (const o of [
      this.decorados,
      this.ventanas,
      this.remates,
      this.antenas,
      this.balizas,
      this.carteles,
    ]) {
      o.instanceMatrix.needsUpdate = true;
      if (o.instanceColor) o.instanceColor.needsUpdate = true;
    }
  }

  /**
   * Nubes y asteroides: grumos de tres bolas por sitio.
   *
   * Tres bolas descolocadas entre si, cada una con su escala, hacen una forma
   * irregular. Una sola bola se lee como una pelota, y una caja —que es lo que
   * habia— se lee como una caja, da igual de que color sea.
   */
  private colocarGrumos(
    objeto: InstancedMesh,
    p: PaletaBioma,
    esRoca: boolean
  ): void {
    const formas: Array<{ m: Matrix4; base: number }> = [];
    const m = new Matrix4();
    const color = new Color();
    const q = new Quaternion();
    const euler = new Euler();
    const escala = new Vector3();

    for (let i = 0; i < 24; i++) {
      const lado = i % 2 === 0 ? -1 : 1;
      const fila = Math.floor(i / 2);
      const z = fila * 22;
      const x = lado * (10 + ((i * 3) % 8));
      const y = 6 + ((i * 4) % 12);
      const tamano = esRoca ? 2.2 + ((i * 2) % 4) : 3 + ((i * 3) % 5);

      for (let b = 0; b < 3; b++) {
        const k = i * 3 + b;
        // Desplazamientos deterministas: el mismo sitio se ve igual siempre,
        // que es lo que hace que el paisaje se reconozca.
        const dx = ((k * 7) % 5) - 2;
        const dy = ((k * 3) % 3) - 1;
        const dz = ((k * 11) % 5) - 2;
        const s = tamano * (0.55 + ((k * 13) % 5) / 10);

        escala.set(s, s * (esRoca ? 0.85 : 0.62), s);
        // A las rocas se les da un giro para que no se vea que todas son la
        // misma bola; las nubes no lo necesitan porque son mas suaves.
        euler.set(esRoca ? (k % 7) * 0.4 : 0, (k % 5) * 0.6, esRoca ? (k % 3) * 0.5 : 0);
        q.setFromEuler(euler);
        m.compose(
          new Vector3(x + dx, y + dy, z + dz * 1.5),
          q,
          escala
        );
        objeto.setMatrixAt(k, m);
        formas.push({ m: m.clone(), base: z + dz * 1.5 });
        color.setHex(p.decorado[k % p.decorado.length] as number);
        objeto.setColorAt(k, color);
      }
    }
    objeto.instanceMatrix.needsUpdate = true;
    if (objeto.instanceColor) objeto.instanceColor.needsUpdate = true;
    if (esRoca) this.formasAsteroide = formas;
    else this.formasNube = formas;
  }

  // ------------------------------------------------------------------
  // Reserva de objetos
  // ------------------------------------------------------------------

  private sacarDeReserva(tipo: keyof Plantillas): Object3D {
    const pila = this.reserva.get(tipo);
    if (pila && pila.length > 0) {
      const o = pila.pop() as Object3D;
      o.visible = true;
      return o;
    }
    const nuevo = this.plantillas[tipo].clone();
    this.escena.add(nuevo);
    return nuevo;
  }

  private devolverAReserva(tipo: string, o: Object3D): void {
    o.visible = false;
    let pila = this.reserva.get(tipo);
    if (!pila) {
      pila = [];
      this.reserva.set(tipo, pila);
    }
    pila.push(o);
  }

  // ------------------------------------------------------------------
  // Ciclo de vida de la partida
  // ------------------------------------------------------------------

  iniciar(seed: number, recordPersonal = 0, multiplicadorBase = 1): void {
    this.limpiarPista();

    this.msJugados = 0;
    this.revivires = 0;
    this.cerrada = false;
    // Solo 1 o 2: un multiplicador que llegara corrupto no puede convertirse en
    // monedas infinitas. Y la cota del servidor solo se dobla, nunca mas.
    this.multiplicadorBase = multiplicadorBase === 2 ? 2 : 1;

    this.seed = seed >>> 0;
    this.distancia = 0;
    this.monedas = 0;
    this.combo = 0;
    this.msDesdeCombo = 0;
    this.carril = 0;
    this.xActual = 0;
    this.y = 0;
    this.vy = 0;
    this.deslizando = 0;
    this.escudo = false;
    this.tIman = 0;
    this.tX2 = 0;
    this.tCohete = 0;
    this.muertePor = null;
    this.ultimoSegmentoGenerado = -1;
    this.biomaActual = 'ciudad';
    this.sacudida = 0;
    this.tiempoJuego = 0;
    this.empujeBorde = 0;
    this.recordPersonal = recordPersonal;
    this.avisoCasiDado = false;
    this.vivo = true;
    this.pausada = false;
    this.inicioMs = performance.now();

    this.jugador.position.set(0, 0, 0);
    this.jugador.rotation.set(0, 0, 0);
    this.jugador.scale.set(1, 1, 1);
    this.jugador.visible = true;

    this.entrada.reiniciar(this.inicioMs);
    this.entrada.conectar(this.alActuar, () => audio.despertar());
    this.aplicarPaleta(PALETAS.ciudad);
    this.recolocarDecorados();
    this.generarAdelante();

    this.ultimoCuadro = performance.now();
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = requestAnimationFrame(this.bucle);
    this.emitirEstado();
  }

  private limpiarPista(): void {
    for (const p of this.activas) this.devolverAReserva(p.tipo, p.objeto);
    this.activas = [];
  }

  pausar(): void {
    this.pausada = true;
  }

  reanudar(): void {
    if (!this.vivo) return;
    this.pausada = false;
    // Sin esto, el dt del primer cuadro tras la pausa seria enorme y el jugador
    // aparecerian cien metros mas adelante, probablemente dentro de un obstaculo.
    this.ultimoCuadro = performance.now();
  }

  /** Mata la partida sin choque: el jugador cerro la app o se fue.
   *
   * Funciona tambien si el jugador ya estaba muerto esperando a decidir si
   * revive. Si solo funcionara estando vivo, irse al inicio desde el dialogo
   * de revivir dejaria la partida abierta para siempre en el servidor.
   */
  /**
   * Cierra la partida porque el jugador se va.
   *
   * **Devuelve si de verdad ha cerrado algo.** Importa: quien llama a esto
   * suele querer silenciar el resumen de la partida, y solo tiene sentido
   * silenciarlo si va a haber un resumen que silenciar. Si la partida ya
   * estaba cerrada aqui no pasa nada, y quien llama tiene que saberlo.
   */
  abandonar(): boolean {
    if (this.cerrada) return false;
    if (this.vivo) this.muertePor = 'abandono';
    this.terminar();
    return true;
  }

  destruir(): void {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.entrada.desconectar();
    window.removeEventListener('resize', this.redimensionar);
    document.removeEventListener('visibilitychange', this.alCambiarVisibilidad);
    this.render.dispose();
  }

  /**
   * Revivir tras ver un anuncio: sigue la misma partida donde murio.
   *
   * Solo se llama cuando **el servidor ya lo ha concedido**. El motor no decide
   * si se puede revivir ni cuantas veces: cuenta el servidor, que es el unico
   * que sabe si el anuncio se vio de verdad.
   */
  revivir(): void {
    if (this.vivo) return;
    this.vivo = true;
    this.pausada = false;
    this.revivires += 1;
    this.muertePor = null;
    this.escudo = true; // un escudo de regalo para que no muera otra vez al instante
    this.y = 0;
    this.vy = 0;
    this.deslizando = 0;
    this.jugador.visible = true;
    this.jugador.rotation.set(0, 0, 0);
    this.jugador.scale.set(1, 1, 1);
    // Se limpia lo que tenga delante: revivir dentro de un tiburon no es revivir.
    const limpioHasta = this.distancia + 26;
    this.activas = this.activas.filter((p) => {
      if (p.z > -limpioHasta && !p.consumida && this.esObstaculo(p.tipo)) {
        this.devolverAReserva(p.tipo, p.objeto);
        return false;
      }
      return true;
    });
    // **Volver a escuchar los toques.** `morir` desconecta la entrada para que
    // los toques del dialogo del anuncio no se cuelen en el registro de la
    // partida; sin esta linea el jugador revive y no puede moverse, que es
    // peor que no revivir. El registro NO se reinicia: el servidor tiene que
    // ver los toques de toda la partida, antes y despues de revivir.
    this.entrada.conectar(this.alActuar, () => audio.despertar());
    this.ultimoCuadro = performance.now();
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = requestAnimationFrame(this.bucle);
    this.emitirEstado();
  }

  // ------------------------------------------------------------------
  // Entrada
  // ------------------------------------------------------------------

  private alActuar = (a: Accion): void => {
    if (!this.vivo || this.pausada) return;
    switch (a) {
      // Al llegar al carril del borde, el juego avisa con un toque en vez de no
      // hacer nada. Sin aviso, "estoy en el borde" y "el juego no me ha oido"
      // se sienten igual, y el dueno reporto justo eso el 1-oct-2026.
      case Accion.Izquierda:
        if (this.carril > -1) {
          this.carril = (this.carril - 1) as Carril;
          telegram.seleccion();
        } else {
          this.avisarBorde(-1);
        }
        break;
      case Accion.Derecha:
        if (this.carril < 1) {
          this.carril = (this.carril + 1) as Carril;
          telegram.seleccion();
        } else {
          this.avisarBorde(1);
        }
        break;
      case Accion.Salto:
        if (puedeSaltar(this.y, this.deslizando > 0)) {
          this.vy = SALTO_V0;
          audio.salto();
          telegram.vibrar('light');
        }
        break;
      case Accion.Deslizar:
        if (this.y > 0.01) {
          // Deslizarse en el aire: baja de golpe. Se siente bien y sirve para
          // caer rapido sobre una moneda.
          this.vy = -GRAVEDAD * 0.55;
        } else if (this.deslizando <= 0) {
          this.deslizando = DUR_DESLIZAR;
          audio.deslizar();
          telegram.vibrar('soft');
        }
        break;
    }
  };

  /**
   * El jugador quiso salirse por un lado y ya estaba en el borde.
   * Se le contesta: una vibracion seca y un empujoncito del personaje hacia ese
   * lado, que vuelve solo. Es la diferencia entre "no puedo" y "no te oi".
   */
  private avisarBorde(lado: -1 | 1): void {
    telegram.vibrar('rigid');
    this.empujeBorde = lado * 0.3;
  }

  // ------------------------------------------------------------------
  // Bucle
  // ------------------------------------------------------------------

  private bucle = (ahora: number): void => {
    this.rafId = requestAnimationFrame(this.bucle);
    if (this.pausada) {
      this.ultimoCuadro = ahora;
      return;
    }

    // Tope al dt: si el movil se queda atascado o la pestana vuelve del fondo,
    // un dt de un segundo teletransportaria al jugador dentro de un obstaculo.
    const dt = Math.min((ahora - this.ultimoCuadro) / 1000, 1 / 20);
    this.ultimoCuadro = ahora;

    this.ultimoDt = dt;
    this.ajustarResolucion(dt);

    if (this.vivo) {
      // El tiempo JUGADO, que es el que se le declara al servidor. No incluye
      // las pausas (ya se ha salido arriba) ni los segundos del anuncio de
      // revivir (mientras se ve, `vivo` es falso).
      //
      // Es lo que hace que revivir no afloje la cota de velocidad del
      // antifraude: ver un anuncio no compra distancia permitida
      // (ver DECISIONS.md D-012).
      this.msJugados += dt * 1000;
      this.actualizar(dt);
    } else {
      // Muerto: solo la animacion de la caida y las chispas.
      this.chispas.actualizar(dt);
      this.animarMuerte(dt);
    }

    this.aplicarSacudida(dt);
    this.render.render(this.escena, this.camara);
  };

  private actualizar(dt: number): void {
    this.tiempoJuego += dt;

    // --- Avance ---
    const vBase = velocidadEn(this.distancia);
    const v =
      this.tCohete > 0 ? vBase * cfgNivel.jugador.multiplicadorCohete : vBase;
    this.distancia += v * dt;

    // --- Movimiento lateral ---
    const xObjetivo = this.carril * ANCHO_CARRIL;
    const dx = xObjetivo - this.xActual;
    const paso = VEL_CAMBIO_CARRIL * dt;
    this.xActual += Math.abs(dx) <= paso ? dx : Math.sign(dx) * paso;

    // --- Salto y gravedad ---
    if (this.tCohete > 0) {
      // Con el cohete el jugador va por encima de todo: no hay gravedad.
      this.y += (5.2 - this.y) * Math.min(1, dt * 6);
      this.vy = 0;
    } else {
      this.vy -= GRAVEDAD * dt;
      this.y += this.vy * dt;
      if (this.y <= 0) {
        this.y = 0;
        this.vy = 0;
      }
    }

    if (this.deslizando > 0) this.deslizando -= dt;

    // --- Potenciadores ---
    if (this.tIman > 0) this.tIman -= dt;
    if (this.tX2 > 0) this.tX2 -= dt;
    if (this.tCohete > 0) {
      this.tCohete -= dt;
      if (this.tCohete <= 0) this.vy = 0;
    }

    // --- Combo de monedas: se corta si pasa un segundo sin coger ninguna ---
    this.msDesdeCombo += dt;
    if (this.msDesdeCombo > 1.1 && this.combo > 0) this.combo = 0;

    this.colocarJugador();
    this.generarAdelante();
    this.moverPista();
    this.comprobarColisiones();
    this.actualizarBioma();
    this.chispas.actualizar(dt);
    this.atenuarDestello(dt);

    this.emitirEstado();
  }

  private colocarJugador(): void {
    // El empuje del borde decae solo: el personaje se asoma y vuelve.
    this.empujeBorde *= 0.82;
    if (Math.abs(this.empujeBorde) < 0.005) this.empujeBorde = 0;
    this.jugador.position.x = this.xActual + this.empujeBorde;
    this.jugador.position.y = this.y;

    // Al deslizarse el personaje se achata. Es la lectura mas clara posible de
    // "ahora soy bajo", y no cuesta ni un vertice.
    const achatado = this.deslizando > 0;
    const escalaY = achatado ? 0.48 : 1;
    this.jugador.scale.y += (escalaY - this.jugador.scale.y) * 0.35;
    this.jugador.rotation.x = achatado ? -0.35 : 0;

    // Inclinacion al cambiar de carril: el personaje se tumba hacia donde va.
    const inclina = (this.carril * ANCHO_CARRIL - this.xActual) * -0.12;
    this.jugador.rotation.z += (inclina - this.jugador.rotation.z) * 0.25;

    // Con el cohete, apunta hacia arriba.
    const cabeceo = this.tCohete > 0 ? -0.3 : 0;
    this.jugador.rotation.x += (cabeceo - this.jugador.rotation.x) * 0.2;

    this.animarJugador();
    this.animarCorredor(this.ultimoDt);

    this.sombra.position.set(this.xActual, 0.03, 0);
    const k = Math.max(0.25, 1 - this.y / 6);
    this.sombra.scale.set(k, k, k);
    (this.sombra.material as MeshBasicMaterial).opacity = 0.32 * k;

    // La camara sigue en lateral, suave, y sube un poco con el cohete.
    const camX = this.xActual * 0.42;
    this.camara.position.x += (camX - this.camara.position.x) * 0.1;
    const camY = 4.4 + Math.min(this.y * 0.45, 2.6);
    this.camara.position.y += (camY - this.camara.position.y) * 0.08;
  }

  /** Genera los segmentos que faltan por delante del jugador. */
  private generarAdelante(): void {
    const segActual = Math.floor(this.distancia / LARGO_SEGMENTO);
    const hasta = segActual + SEGMENTOS_ADELANTE;
    while (this.ultimoSegmentoGenerado < hasta) {
      this.ultimoSegmentoGenerado += 1;
      this.materializar(generarSegmento(this.seed, this.ultimoSegmentoGenerado));
    }
  }

  private materializar(seg: Segmento): void {
    for (const o of seg.obstaculos) {
      const objeto = this.sacarDeReserva(o.tipo as keyof Plantillas);
      objeto.position.set(o.carril * ANCHO_CARRIL, 0, -o.z);
      objeto.rotation.y = 0;
      this.activas.push({
        objeto,
        tipo: o.tipo,
        carril: o.carril,
        z: -o.z,
        valor: 0,
        consumida: false,
      });
    }
    for (const r of seg.recolectables) {
      const objeto = this.sacarDeReserva(r.tipo as keyof Plantillas);
      const plantilla = this.plantillas[r.tipo as keyof Plantillas];
      objeto.position.set(r.carril * ANCHO_CARRIL, plantilla.position.y, -r.z);
      this.activas.push({
        objeto,
        tipo: r.tipo,
        carril: r.carril,
        z: -r.z,
        valor: r.valor,
        consumida: false,
      });
    }
  }

  /**
   * Mueve el mundo hacia el jugador. El jugador se queda en z=0 y lo que se
   * desplaza es la pista: asi las coordenadas nunca crecen y no hay perdida de
   * precision en partidas largas.
   */
  private moverPista(): void {
    const d = this.distancia;
    // La calzada, el terreno y los bordillos se mueven juntos: son lisos y
    // largos, asi que basta con deslizarlos y nadie nota donde empiezan.
    const zSuelo = (-d % 40) - 120;
    this.suelo.position.z = zSuelo;
    this.terreno.position.z = zSuelo;
    for (const b of this.bordillos) b.position.z = zSuelo;

    // Las rayas de los carriles: se mueve el objeto entero, no cada raya. Como
    // se repiten cada 8 metros, con desplazarlo entre 0 y 8 basta para que el
    // patron se vea infinito.
    this.lineas.position.z = (d % 8) - 300;

    // El decorado lateral se recicla EDIFICIO A EDIFICIO, no moviendo el grupo.
    //
    // El truco de mover el grupo entero (el que usan las rayas del carril) solo
    // vale cuando todas las piezas son IGUALES. Los edificios no lo son: al
    // dar la vuelta el patron, el edificio que tenias delante se convertia de
    // golpe en otro, con otra altura y otro color. El dueno lo vio jugando el
    // 22-oct-2026: "estas viendo un edificio y de momento cambia de forma".
    //
    // Recolocando cada uno por separado, cada edificio da la vuelta cuando ya
    // ha quedado DETRAS del jugador, que es donde no se ve. Son 24 matrices
    // por cuadro: nada, y sigue siendo una sola llamada de dibujo.
    this.recolocarEnZ(d);

    // Las piezas de la pista: mover y reciclar las que quedaron detras.
    const sobrantes: PiezaActiva[] = [];
    for (const p of this.activas) {
      p.objeto.position.z = p.z + d;
      if (p.objeto.position.z > 12) {
        this.devolverAReserva(p.tipo, p.objeto);
        continue;
      }
      // Giro de las monedas y los potenciadores: nada se queda quieto.
      if (!this.esObstaculo(p.tipo)) {
        p.objeto.rotation.y += 0.06;
        if (p.tipo === 'ballena') p.objeto.rotation.y += 0.01;
      }
      sobrantes.push(p);
    }
    this.activas = sobrantes;
  }

  private esObstaculo(tipo: string): boolean {
    return tipo === 'barrera' || tipo === 'vela' || tipo === 'tiburon';
  }

  private comprobarColisiones(): void {
    // Las reglas de choque y recogida viven en `reglas.ts`, como funciones
    // puras, y se prueban sin navegador (`npm test`). Aqui solo se les da el
    // estado y se actua con lo que contesten.
    const estado: EstadoJugador = {
      x: this.xActual,
      y: this.y,
      deslizando: this.deslizando > 0,
      tCohete: this.tCohete,
    };

    for (const p of this.activas) {
      if (p.consumida) continue;
      const zMundo = p.objeto.position.z;

      // El iman atrae las monedas de los carriles de al lado.
      if (this.tIman > 0 && p.tipo === 'moneda' && zMundo > -RADIO_IMAN && zMundo < 6) {
        const hacia = this.xActual - p.objeto.position.x;
        p.objeto.position.x += hacia * 0.22;
        p.objeto.position.y += (this.y + 1 - p.objeto.position.y) * 0.22;
        if (Math.abs(hacia) < 1.2) {
          this.cogerRecolectable(p);
          continue;
        }
      }

      if (this.esObstaculo(p.tipo)) {
        if (hayChoque(p.tipo as TipoObstaculo, zMundo, p.objeto.position.x, estado)) {
          this.chocar(p);
          return;
        }
        continue;
      }

      if (hayRecogida(zMundo, p.objeto.position.x, p.objeto.position.y, estado)) {
        this.cogerRecolectable(p);
      }
    }
  }

  private chocar(p: PiezaActiva): void {
    p.consumida = true;
    if (this.escudo) {
      this.escudo = false;
      audio.escudoRoto();
      telegram.avisar('warning');
      this.sacudida = 0.3;
      this.flash(0x4ec3ff, 0.5);
      this.chispas.emitir(this.xActual, this.y + 1, 0, COLORES.escudo, 16);
      this.devolverAReserva(p.tipo, p.objeto);
      this.activas = this.activas.filter((x) => x !== p);
      this.emitirEstado();
      return;
    }
    this.muertePor = p.tipo as ResultadoPartida['muertePor'];
    audio.golpe();
    telegram.avisar('error');
    this.sacudida = 0.75;
    this.flash(0xff3b30, 0.7);
    this.chispas.emitir(this.xActual, this.y + 0.9, 0, 0xff6a4a, 22);
    this.morir();
  }

  private cogerRecolectable(p: PiezaActiva): void {
    p.consumida = true;
    this.devolverAReserva(p.tipo, p.objeto);
    this.activas = this.activas.filter((x) => x !== p);

    const tipo = p.tipo as TipoRecolectable;
    switch (tipo) {
      case 'moneda': {
        // Dos multiplicadores distintos que se acumulan: el potenciador del
        // nivel (10 s) y el x2 de toda la partida que concede el anuncio. Son
        // cosas separadas y el jugador las ve separadas, asi que se suman las
        // ventajas en vez de pisarse.
        const multiplicador = (this.tX2 > 0 ? 2 : 1) * this.multiplicadorBase;
        this.monedas += p.valor * multiplicador;
        this.combo += 1;
        this.msDesdeCombo = 0;
        audio.moneda(this.combo - 1);
        this.chispas.emitir(p.objeto.position.x, p.objeto.position.y, 0, COLORES.moneda, 5);
        if (this.combo % 10 === 0) telegram.vibrar('light');
        break;
      }
      case 'ballena': {
        const multiplicador = this.tX2 > 0 ? 2 : 1;
        this.monedas += p.valor * multiplicador;
        audio.ballena();
        telegram.avisar('success');
        this.flash(0x4fa8d8, 0.55);
        this.sacudida = 0.25;
        this.chispas.emitir(p.objeto.position.x, p.objeto.position.y, 0, COLORES.ballena, 30);
        break;
      }
      case 'iman':
        this.tIman = DUR_IMAN;
        audio.potenciador();
        telegram.vibrar('medium');
        this.chispas.emitir(p.objeto.position.x, p.objeto.position.y, 0, COLORES.iman, 10);
        break;
      case 'x2':
        this.tX2 = DUR_X2;
        audio.potenciador();
        telegram.vibrar('medium');
        this.chispas.emitir(p.objeto.position.x, p.objeto.position.y, 0, COLORES.x2, 10);
        break;
      case 'escudo':
        this.escudo = true;
        audio.potenciador();
        telegram.vibrar('medium');
        this.chispas.emitir(p.objeto.position.x, p.objeto.position.y, 0, COLORES.escudo, 10);
        break;
      case 'cohete':
        this.tCohete = DUR_COHETE;
        audio.cohete();
        telegram.vibrar('heavy');
        this.flash(0xff6b35, 0.4);
        this.chispas.emitir(p.objeto.position.x, p.objeto.position.y, 0, COLORES.cohete, 18);
        break;
    }
    this.emitirEstado();
  }

  // ------------------------------------------------------------------
  // Biomas
  // ------------------------------------------------------------------

  private actualizarBioma(): void {
    const seg = Math.floor(this.distancia / LARGO_SEGMENTO);
    const bioma = biomaDeSegmento(seg);
    if (bioma !== this.biomaActual) {
      this.biomaActual = bioma;
      audio.cambioBioma();
      telegram.vibrar('rigid');
      this.recolocarDecorados();
    }
    // Transicion suave al ENTRAR en un bioma: el color viaja durante 120 m en
    // vez de dar un corte seco, que se leeria como un fallo grafico.
    //
    // **Pero solo si de verdad se viene de otro bioma.** Al empezar la partida
    // no se viene de ningun sitio, y mezclar con "el anterior" hacia que los
    // primeros 120 metros de CADA partida se pintaran casi enteros con la
    // paleta del espacio: el jugador arrancaba en una ciudad de noche que se
    // veia morada y sin edificios, y solo despues aparecia la ciudad de verdad.
    //
    // Era lo primero que veia cualquiera que abriese el juego. Llevaba ahi
    // desde la fase 2 y no se noto hasta que las paletas se diferenciaron de
    // verdad (1-oct-2026): con los tres biomas en gris azulado, mezclar uno con
    // otro no se veia.
    const primerBioma = this.distancia < LARGO_BIOMA;
    if (primerBioma) {
      this.aplicarPaleta(PALETAS[bioma]);
      return;
    }
    const metrosEnBioma = this.distancia % LARGO_BIOMA;
    const t = Math.min(1, metrosEnBioma / 120);
    const anterior = this.biomaAnterior(bioma);
    this.aplicarPaleta(mezclarPaletas(PALETAS[anterior], PALETAS[bioma], t));
  }

  private biomaAnterior(actual: Bioma): Bioma {
    const orden: Bioma[] = ['ciudad', 'estratosfera', 'espacio'];
    const i = orden.indexOf(actual);
    return orden[(i + orden.length - 1) % orden.length] as Bioma;
  }

  private aplicarPaleta(p: PaletaBioma): void {
    // El fondo lo pinta la cupula del cielo, no un color plano. El color de
    // limpieza se deja en el cenit por si algun cuadro se cuela por detras.
    //
    // Se reutiliza el mismo objeto Color en vez de crear uno nuevo: esto corre
    // en cada cuadro durante las transiciones de bioma, y un objeto por cuadro
    // son 60 objetos por segundo para el recolector de basura. Una recoleccion
    // a 60 fps es un tiron que se ve.
    this.colorFondo.setHex(p.cenit);
    this.escena.background = this.colorFondo;
    this.cielo.pintar(p.cenit, p.horizonte, p.curvaCielo);

    this.niebla.color.setHex(p.niebla);
    this.niebla.near = p.nieblaCerca;
    this.niebla.far = p.nieblaLejos;

    (this.suelo.material as MeshLambertMaterial).color.setHex(p.suelo);
    (this.lineas.material as MeshBasicMaterial).color.setHex(p.lineas);
    for (const r of this.railes) (r.material as MeshBasicMaterial).color.setHex(p.railes);

    this.luzAmbiente.color.setHex(p.luzAmbiente);
    this.luzAmbiente.intensity = p.intensidadAmbiente;
    this.luzSol.color.setHex(p.luzDireccional);
    this.luzSol.intensity = p.intensidadDireccional;
    this.luzContorno.color.setHex(p.luzContorno);
    this.luzContorno.intensity = p.intensidadContorno;

    // La Luna toma el color del horizonte: asi pertenece al cielo en el que
    // esta en vez de parecer pegada encima. En la ciudad se ve poco (hay
    // contaminacion luminica) y en el espacio a plena luz, que es la historia
    // que cuenta el viaje.
    this.luna.pintar(p.horizonte, p.estrellas ? 0.95 : 0.45);

    const matEstrellas = this.estrellas.material as { opacity: number };
    const objetivo = p.estrellas ? 0.85 : 0;
    matEstrellas.opacity += (objetivo - matEstrellas.opacity) * 0.05;
  }

  // ------------------------------------------------------------------
  // Juice
  // ------------------------------------------------------------------

  private flash(color: number, fuerza: number): void {
    const m = this.destello.material as MeshBasicMaterial;
    m.color.setHex(color);
    m.opacity = fuerza;
    this.destello.visible = fuerza > 0;
  }

  private atenuarDestello(dt: number): void {
    if (!this.destello.visible) return;
    const m = this.destello.material as MeshBasicMaterial;
    m.opacity = Math.max(0, m.opacity - dt * 2.2);
    // Se APAGA al llegar a cero, no solo se vuelve transparente: un plano
    // transparente a pantalla completa cuesta lo mismo pintarlo invisible que
    // visible, y el 99 % del tiempo esta invisible.
    if (m.opacity <= 0) this.destello.visible = false;
  }

  private aplicarSacudida(dt: number): void {
    if (this.sacudida <= 0) return;
    this.sacudida = Math.max(0, this.sacudida - dt * 2.4);
    const f = this.sacudida * 0.42;
    this.camara.position.x += (Math.random() - 0.5) * f;
    this.camara.position.y += (Math.random() - 0.5) * f;
    this.camara.rotation.z = (Math.random() - 0.5) * f * 0.1;
  }

  private animarMuerte(dt: number): void {
    // El personaje cae y se encoge. Corto: lo que el jugador quiere es volver.
    this.jugador.rotation.x -= dt * 3.2;
    this.jugador.position.y = Math.max(0.1, this.jugador.position.y - dt * 4);
    const s = Math.max(0.2, this.jugador.scale.x - dt * 0.8);
    this.jugador.scale.set(s, s, s);
    this.atenuarDestello(dt);
  }

  // ------------------------------------------------------------------
  // Fin
  // ------------------------------------------------------------------

  /**
   * El jugador ha muerto, pero la partida **todavia no se cierra**.
   *
   * Esta es la diferencia que trae revivir, y es la razon de que `terminar` se
   * haya partido en dos. Antes, chocar llamaba a `terminar`, que avisaba a la
   * interfaz y esta cerraba la partida en el servidor. Con eso, revivir era
   * imposible: la partida ya estaba cerrada cuando el jugador decidia.
   *
   * Ahora chocar llama aqui, y es la interfaz la que decide: si ofrece revivir
   * y el jugador acepta, llama a `revivir`; si no, llama a `rendirse`, y
   * entonces se cierra.
   *
   * La entrada se desconecta: los toques en el dialogo del anuncio no son
   * toques de la partida, y colarlos en el registro desviaria el replay del
   * antifraude.
   */
  private morir(): void {
    if (!this.vivo) return;
    this.vivo = false;

    this.entrada.desconectar();
    this.alMorir?.({
      distancia: Math.floor(this.distancia),
      monedas: this.monedas,
      revivires: this.revivires,
      muertePor: this.muertePor,
    });
    this.emitirEstado();
  }

  /**
   * El jugador no revive: se cierra la partida de verdad.
   *
   * Lo llama la interfaz cuando el jugador decide no ver el anuncio, cuando no
   * le quedan revivires o cuando los anuncios estan apagados.
   */
  rendirse(): void {
    this.terminar();
  }

  private terminar(): void {
    // La guarda es `cerrada` y no `vivo`, porque ahora se puede llegar aqui
    // estando muerto: entre morir y cerrar hay un dialogo de anuncio por medio.
    // Lo que no puede pasar dos veces es CERRAR, que es lo que manda el
    // resultado al servidor.
    if (this.cerrada) return;
    this.cerrada = true;
    this.vivo = false;

    this.entrada.desconectar();

    const distanciaFinal = Math.floor(this.distancia);

    // "Casi lo logras": si se queda a menos del 8% de su record, se le dice.
    // Es la frase que hace que se juegue otra partida.
    if (
      this.recordPersonal > 0 &&
      distanciaFinal < this.recordPersonal &&
      distanciaFinal > this.recordPersonal * 0.92 &&
      !this.avisoCasiDado
    ) {
      this.avisoCasiDado = true;
      audio.casi();
      this.alCasiRecord?.();
    } else if (distanciaFinal > this.recordPersonal) {
      audio.record();
      telegram.avisar('success');
    }

    this.alTerminar?.({
      distancia: distanciaFinal,
      monedas: this.monedas,
      // El tiempo JUGADO, no el reloj de pared. La diferencia es el anuncio de
      // revivir y las pausas, y declararla haria que ver un anuncio comprase
      // distancia permitida en la cota del antifraude (DECISIONS.md D-012).
      duracionMs: Math.round(this.msJugados),
      registroEntradas: this.entrada.registroCompacto(),
      segmentos: Math.floor(this.distancia / LARGO_SEGMENTO) + 1,
      muertePor: this.muertePor,
      revivires: this.revivires,
    });
    this.emitirEstado();
  }

  private emitirEstado(): void {
    this.alCambiarEstado?.({
      distancia: Math.floor(this.distancia),
      monedas: this.monedas,
      velocidad: velocidadEn(this.distancia),
      bioma: this.biomaActual,
      combo: this.combo,
      escudo: this.escudo,
      msX2: Math.max(0, this.tX2 * 1000),
      msIman: Math.max(0, this.tIman * 1000),
      msCohete: Math.max(0, this.tCohete * 1000),
      vivo: this.vivo,
    });
  }

  // ------------------------------------------------------------------
  // Pantalla
  // ------------------------------------------------------------------

  private redimensionar = (): void => {
    const ancho = this.lienzo.clientWidth || window.innerWidth;
    const alto = this.lienzo.clientHeight || telegram.altoViewport;
    this.render.setSize(ancho, alto, false);
    this.camara.aspect = ancho / Math.max(1, alto);
    // En vertical, un campo de vision mas ancho deja ver los tres carriles.
    this.camara.fov = this.camara.aspect < 0.75 ? 72 : 62;
    this.camara.updateProjectionMatrix();
  };

  // ------------------------------------------------------------------
  // Resolucion adaptativa
  // ------------------------------------------------------------------

  /**
   * Ajusta la resolucion de render para mantener 60 fps.
   *
   * **Por que hace falta.** Este juego no es pesado de geometria: 10.000
   * triangulos no son nada. Lo que le cuesta es el RELLENO, o sea, pintar
   * pixeles: cielo, suelo, transparencias y halos, todo a pantalla completa.
   * Y eso escala con el numero de pixeles, no con lo que hay en la escena.
   *
   * Medido el 1-oct-2026 en una grafica integrada Intel UHD: **60 fps a 0,32
   * megapixeles y 30 a 1,2**. Un movil de gama media esta en ese mismo orden.
   *
   * **Por que adaptativo y no un numero fijo.** Poner un tope fijo obliga a
   * elegir entre que se vea borroso en un movil bueno o que vaya a tirones en
   * uno malo, y no hay forma de saber de antemano en cual esta cada jugador. El
   * numero de nucleos o el modelo del telefono no sirven: lo unico que dice la
   * verdad es cuanto tarda de verdad en pintar un cuadro, y eso solo se sabe
   * midiendolo.
   *
   * La escala solo se mueve a saltos y como mucho cada segundo y medio: cambiar
   * la resolucion reasigna los buferes de la tarjeta grafica, asi que hacerlo
   * a menudo seria peor que el problema.
   *
   * **Y comprueba que bajar sirva de algo.** Esta es la parte que importa y la
   * que casi me dejo fuera: si los cuadros van lentos por algo que NO son los
   * pixeles —el procesador, el navegador, otra app comiendose el telefono—,
   * bajar la resolucion emborrona el juego y no arregla nada. Asi que despues
   * de bajar se mide otra vez: si no ha mejorado, se devuelve la nitidez y se
   * deja de intentar.
   *
   * Se vio en pruebas el 1-oct-2026: en un entorno que tenia su propio tope de
   * 30 fps, la version sin esta comprobacion bajaba hasta el minimo y dejaba el
   * juego borroso para siempre, sin ganar un solo fotograma.
   */
  private ajustarResolucion(dt: number): void {
    this.msMedidos += dt * 1000;
    this.cuadrosMedidos += 1;
    if (this.cuadrosMedidos < 45) return;

    const medio = this.msMedidos / this.cuadrosMedidos;
    this.msMedidos = 0;
    this.cuadrosMedidos = 0;

    const ahora = performance.now();
    if (ahora - this.ultimoAjuste < 1500) return;

    // ¿La ultima bajada sirvio de algo? Se exige una mejora real (2 ms); si no
    // la hubo, el cuello de botella no son los pixeles.
    if (this.msAntesDeBajar > 0) {
      const mejoro = medio < this.msAntesDeBajar - 2;
      this.msAntesDeBajar = 0;
      if (!mejoro) {
        this.bajarNoSirve = true;
        this.escalaPixeles = this.escalaAntesDeBajar;
        this.ultimoAjuste = ahora;
        this.render.setPixelRatio(this.escalaPixeles);
        this.redimensionar();
        return;
      }
    }

    const antes = this.escalaPixeles;

    // 20 ms es ~50 fps: por debajo de eso ya se nota el tiron, asi que se baja.
    if (medio > 20 && this.escalaPixeles > 0.75 && !this.bajarNoSirve) {
      this.escalaAntesDeBajar = this.escalaPixeles;
      this.msAntesDeBajar = medio;
      this.escalaPixeles = Math.max(0.75, this.escalaPixeles - 0.25);
    } else if (medio < 13 && this.escalaPixeles < this.escalaMaxima) {
      // 13 ms es ~77 fps: hay margen de sobra, se puede dar mas nitidez.
      this.escalaPixeles = Math.min(this.escalaMaxima, this.escalaPixeles + 0.25);
    }

    if (this.escalaPixeles !== antes) {
      this.ultimoAjuste = ahora;
      this.render.setPixelRatio(this.escalaPixeles);
      this.redimensionar();
    }
  }

  /** Si el jugador se va a otra app, se pausa. Nadie quiere morir por una llamada. */
  private alCambiarVisibilidad = (): void => {
    if (document.hidden) this.pausar();
  };
}
